import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  MemecoinComment,
  MemecoinCommentReaction,
  TwitterUserMetadata,
} from '../../../../types/reducerTypes'
import { Box } from '../../../../shared_components/UI/Box'

import styled from 'styled-components'
import AddReplyModal from './AddReplyModal'
import TimeAgo from 'react-timeago'
import { obfuscateEthWalletAddress } from '../../../../shared_components/SocialConnectButtons/Twitter'
import EmojiPicker, { Theme } from 'emoji-picker-react'
import {
  useAddMemecoinCommentReactionMutation,
  useAddMemecoinCommentReplyReactionMutation,
} from '../../../../modules/memecoins/api'
import { useDispatch } from 'react-redux'
import {
  appendMemecoinCommentReaction,
  appendMemecoinCommentReplyReaction,
} from '../../../../modules/memecoins/slice'
import useTwitterProfileImage from '../../../../hooks/useTwitterProfileImage'
import { Span, Strong } from '../../../../shared_components/Library/Typeography'

export const Seperator = styled.div`
  width: 100%;
  background: #c4c4c4;
  opacity: 0.1;
  height: 1px;
  margin-bottom: 16px;
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  padding: 4px 8px 4px 8px;
  margin-bottom: 10px;
  position: relative;
  border-radius: var(--units-88, 88px);
  background: var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04));
  :hover {
    border-radius: var(--units-88, 88px);
    border: 1px solid var(--stroke-secondary, #333);
    background: var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04));
  }
`

const Emoji = styled(Box)`
  border-radius: 22px;
  text-align: center;
  background: #161c20;
  padding: 4px 8px 4px 8px;
  font-size: 12px;
`

const EmojiPickerWrapper = styled.div`
  position: absolute;
  top: 100%; // Position below the ActionButton
  z-index: 1000;
`

const EmojiContainer = styled.div`
  position: 'relative';
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  > span {
    font-size: 8px;
  }
`

const Avatar = styled.img`
  width: var(--units-40, 40px);
  height: var(--units-40, 40px);
  border-radius: var(--units-4, 4px);
`

const CommentUsername = ({
  username,
  walletAddress,
}: {
  username?: string
  walletAddress: string
}) => {
  if (username) {
    return (
      <Strong s="L">
        <a
          href={`https://x.com/${username}`}
          target="_blank"
          rel="noreferrer"
          title={username}
          onClick={(e) => e.stopPropagation()}
        >
          {`${username}`}
        </a>
      </Strong>
    )
  }

  return <Strong s="L">{obfuscateEthWalletAddress(walletAddress)}</Strong>
}

const CommentProfileImage = ({
  metadata,
}: {
  metadata: TwitterUserMetadata
}) => {
  const { verified } = metadata ?? {}
  const { url } = useTwitterProfileImage({
    metadata: { twitter: metadata },
  })
  return (
    <ProfileImageCont>
      <ProfileImage $url={url} />
      {verified && <VerifiedBadge />}
    </ProfileImageCont>
  )
}

const CommentTime = ({ time }: { time: string | Date }) => (
  <Span s="S">
    <TimeAgo date={time} />
  </Span>
)

const CommentReactions = ({
  countByEmoji,
  onClick,
}: {
  countByEmoji: { [key: string]: number }
  onClick: ({ emoji }: { emoji: string }) => void
}) => {
  return Object.entries(countByEmoji).map(
    ([emoji, count]: [string, number]) => (
      <Box
        ac
        g={4}
        key={emoji}
        style={{ cursor: 'pointer' }}
        onClick={() => onClick({ emoji })}
      >
        <Emoji ac jc g={2}>
          <span>{emoji}</span>
          <span>{count}</span>
        </Emoji>
      </Box>
    ),
  )
}

export const ReplyView = ({ reply }) => {
  const [isReplyReactionOpen, setIsReplyReactionOpen] = useState(false)
  const emojiPickerRef = useRef(null)
  const [addReaction] = useAddMemecoinCommentReplyReactionMutation()
  const dispatch = useDispatch()

  const { walletAddress, metadata } = reply.author
  const { username, verified, profile_image_url } = metadata || {}

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setIsReplyReactionOpen(false)
      }
    }

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setIsReplyReactionOpen(false)
      }
    }

    if (isReplyReactionOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscKey)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscKey)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [isReplyReactionOpen])

  const handleOnClickReaction = useCallback(
    async ({ emoji }: { emoji: string }) => {
      try {
        const { reaction } = await addReaction({
          data: {
            replyId: reply._id,
            emoji,
          },
        }).unwrap()
        dispatch(appendMemecoinCommentReplyReaction(reaction))
      } catch (error) {
        console.error(error)
      } finally {
        setIsReplyReactionOpen(false)
      }
    },
    [addReaction, dispatch],
  )

  const hasReactions = reply.reactions?.length > 0

  const countByEmoji = reply.reactions.reduce(
    (acc: { [key: string]: number }, reaction: MemecoinCommentReaction) => {
      const { emoji } = reaction
      if (acc[emoji]) {
        acc[emoji] += 1
      } else {
        acc[emoji] = 1
      }
      return acc
    },
    {},
  )

  return (
    <CellCont>
      <CommentProfileImage metadata={metadata} />
      <InfoCont>
        <Box ac g={8}>
          <CommentUsername username={username} walletAddress={walletAddress} />
          <CommentTime time={reply.createdAt} />
        </Box>
        {reply.content && <Text>{reply.content}</Text>}
        <Box g={12} style={{ position: 'relative' }}>
          <ActionButton onClick={() => setIsReplyReactionOpen(true)}>
            <img src="/emoji-smile.svg" alt="next" />
          </ActionButton>
          <EmojiPickerWrapper ref={emojiPickerRef}>
            <EmojiPicker
              reactionsDefaultOpen
              theme={Theme.DARK}
              open={isReplyReactionOpen}
              onEmojiClick={handleOnClickReaction}
              lazyLoadEmojis
            />
          </EmojiPickerWrapper>
          <EmojiContainer>
            {hasReactions && (
              <CommentReactions
                countByEmoji={countByEmoji}
                onClick={handleOnClickReaction}
              />
            )}
          </EmojiContainer>
        </Box>
      </InfoCont>
    </CellCont>
  )
}

export const CommentView = ({ comment }: { comment: MemecoinComment }) => {
  const { walletAddress, metadata } = comment.author
  const { username, verified, profile_image_url } = metadata || {}
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCommentReactionOpen, setIsCommentReactionOpen] = useState(false)
  const [addReaction] = useAddMemecoinCommentReactionMutation()
  const emojiPickerRef = useRef(null)
  const dispatch = useDispatch()

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setIsCommentReactionOpen(false)
      }
    }

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setIsCommentReactionOpen(false)
      }
    }

    if (isCommentReactionOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscKey)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscKey)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [isCommentReactionOpen])

  const handleOnClickReaction = useCallback(
    async ({ emoji }: { emoji: string }) => {
      try {
        const { reactions } = await addReaction({
          data: {
            commentId: comment._id,
            emoji,
          },
        }).unwrap()

        dispatch(
          appendMemecoinCommentReaction({ reactions, commentId: comment._id }),
        )
      } catch (error) {
        console.error(error)
      } finally {
        setIsCommentReactionOpen(false)
      }
    },
    [addReaction],
  )

  const hasReactions = comment.reactions.length > 0

  const countByEmoji = comment.reactions?.reduce(
    (acc: { [key: string]: number }, reaction: MemecoinCommentReaction) => {
      const { emoji } = reaction
      if (acc[emoji]) {
        acc[emoji] += 1
      } else {
        acc[emoji] = 1
      }
      return acc
    },
    {},
  )

  return (
    <CellCont>
      <CommentProfileImage metadata={metadata} />
      <InfoCont>
        <Box ac g={12}>
          <CommentUsername username={username} walletAddress={walletAddress} />
          <CommentTime time={comment.createdAt} />
        </Box>
        {comment.content && <Text>{comment.content}</Text>}
        <Box fw dc g={12} style={{ position: 'relative' }}>
          <Box fw g={12}>
            <ActionButton onClick={() => setIsModalOpen(!isModalOpen)}>
              <img src="/comment-icon.svg" alt="next" />
            </ActionButton>
            <ActionButton onClick={() => setIsCommentReactionOpen(true)}>
              <img src="/emoji-smile.svg" alt="next" />
            </ActionButton>
            <EmojiPickerWrapper ref={emojiPickerRef}>
              <EmojiPicker
                reactionsDefaultOpen
                theme={Theme.DARK}
                open={isCommentReactionOpen}
                onEmojiClick={handleOnClickReaction}
                skinTonesDisabled
              />
            </EmojiPickerWrapper>
            <EmojiContainer>
              {hasReactions && (
                <CommentReactions
                  countByEmoji={countByEmoji}
                  onClick={handleOnClickReaction}
                />
              )}
            </EmojiContainer>
          </Box>
          <AddReplyModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            comment={comment}
          />
        </Box>
        {!!comment.replies.length && (
          <Box fw dc style={{ marginTop: '10px' }}>
            {!!comment.replies.length &&
              comment.replies.map((reply) => (
                <ReplyView reply={reply} key={reply._id} />
              ))}
          </Box>
        )}
      </InfoCont>
    </CellCont>
  )
}

const CellCont = styled(Box)`
  position: relative;
  display: flex;
  gap: 13px;
  width: 100%;
`

const ProfileImageCont = styled.div`
  display: flex;
  // align-items: center;
`

const VerifiedBadge = styled.div`
  width: var(--units-24, 24px);
  height: var(--units-24, 24px);
  background: url('/memecoin-launcher/verified-badge.svg') no-repeat;
  background-size: contain;
  margin-left: -10px;
`

const ProfileImage = styled.div<{ $url: string }>`
  background: url(${({ $url }) => $url}) lightgray 50% / cover no-repeat;
  width: var(--units-40, 40px);
  height: var(--units-40, 40px);
  border-radius: var(--units-4, 4px);
`

const InfoCont = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  position: relative;
`

const Username = styled.div`
  color: var(--text-primary, #fff);

  /* Web/Text/L/Semibold */
  font-family: var(--type-family-space-grotesk, 'Space Grotesk');
  font-size: var(--Text-L-L-size, 16px);
  font-style: normal;
  font-weight: var(--type-weight-Semibold, 600);
  line-height: var(--Text-L-L-line-height, 20px); /* 125% */
`

const Text = styled.div`
  color: var(--text-secondary, #b1b1b1);

  /* Web/Text/M/Regular */
  font-family: var(--type-family-space-grotesk, 'Space Grotesk');
  font-style: normal;
  font-weight: var(--type-weight-Regular, 300);
`

function MemecoinThreadList({ comments }: { comments: MemecoinComment[] }) {
  return (
    <Box dc g={16}>
      {comments.map((comment) => (
        <Box dc fw>
          <CommentView key={comment._id} comment={comment} />
          <Seperator />
        </Box>
      ))}
    </Box>
  )
}

export default MemecoinThreadList
